import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ChannelModeCtx = createContext(null);

/**
 * ChannelModeProvider — global context to track whether the user is
 * operating in "whatsapp" mode or "email" mode.
 *
 * Persisted in localStorage so the choice survives page reloads.
 */
export const ChannelModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    try {
      return localStorage.getItem('channelMode') || 'whatsapp';
    } catch {
      return 'whatsapp';
    }
  });

  const setMode = useCallback((newMode) => {
    const m = newMode === 'email' ? 'email' : 'whatsapp';
    setModeState(m);
    try { localStorage.setItem('channelMode', m); } catch { /* noop */ }
  }, []);

  const toggleMode = useCallback(() => {
    setModeState(prev => {
      const next = prev === 'whatsapp' ? 'email' : 'whatsapp';
      try { localStorage.setItem('channelMode', next); } catch { /* noop */ }
      return next;
    });
  }, []);

  const isEmail = mode === 'email';
  const isWhatsApp = mode === 'whatsapp';

  const value = useMemo(() => ({
    mode, setMode, toggleMode, isEmail, isWhatsApp,
  }), [mode, setMode, toggleMode, isEmail, isWhatsApp]);

  return (
    <ChannelModeCtx.Provider value={value}>
      {children}
    </ChannelModeCtx.Provider>
  );
};

export const useChannelMode = () => {
  const ctx = useContext(ChannelModeCtx);
  if (!ctx) throw new Error('useChannelMode must be inside ChannelModeProvider');
  return ctx;
};

export default ChannelModeCtx;
